import React, { useCallback, useState } from 'react';
import {
  DragEndEvent,
  DragStartEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import * as api from '../services/ClusteringLabService';
import { getContiguousRange } from '../pages/ClusteringLabPage';
import { ClusteringLabMembership } from '../types';

interface UseClusteringLabDndArgs {
  userLid: number | null;
  selected: Set<string>;
  membershipByPhoto: Map<string, string>;
  memberships: ClusteringLabMembership[];
  allVisiblePhotoIds: string[];
  setMemberships: React.Dispatch<React.SetStateAction<ClusteringLabMembership[]>>;
  clearSelection: () => void;
}

export function useClusteringLabDnd({
  userLid,
  selected,
  membershipByPhoto,
  memberships,
  allVisiblePhotoIds,
  setMemberships,
  clearSelection,
}: UseClusteringLabDndArgs) {
  const [activePhotoId, setActivePhotoId] = useState<string | null>(null);
  const [draggedPhotoIds, setDraggedPhotoIds] = useState<string[]>([]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  const onDragStart = useCallback(
    (event: DragStartEvent) => {
      const photoId = event.active.id as string;
      setActivePhotoId(photoId);
      setDraggedPhotoIds(selected.has(photoId) ? Array.from(selected) : [photoId]);
    },
    [selected]
  );

  const onDragCancel = useCallback(() => {
    setActivePhotoId(null);
    setDraggedPhotoIds([]);
  }, []);

  const onDragEnd = useCallback(
    (event: DragEndEvent) => {
      const overId = event.over?.id as string | undefined;
      setActivePhotoId(null);
      setDraggedPhotoIds([]);

      if (!overId || !userLid) return;

      const isGroupTarget = overId.startsWith('group::');
      const isUngroupedTarget = overId.startsWith('ungrouped::');
      if (!isGroupTarget && !isUngroupedTarget) return;

      if (isGroupTarget) {
        const targetGroupId = overId.slice('group::'.length);
        const existingIds = memberships.filter((m) => m.group_id === targetGroupId).map((m) => m.photo_id);
        const combined = [...new Set([...existingIds, ...draggedPhotoIds])];
        const expanded = getContiguousRange(combined, allVisiblePhotoIds);

        const toAssign: string[] = [];
        const toUnassignByGroup = new Map<string, string[]>();

        for (const id of expanded) {
          const sourceGroup = membershipByPhoto.get(id);
          if (sourceGroup === targetGroupId) continue;
          toAssign.push(id);
          if (sourceGroup) {
            const list = toUnassignByGroup.get(sourceGroup) || [];
            list.push(id);
            toUnassignByGroup.set(sourceGroup, list);
          }
        }

        if (toAssign.length === 0) return;

        setMemberships((prev) => {
          const toRemove = new Set(
            Array.from(toUnassignByGroup.entries()).flatMap(([gid, ids]) =>
              ids.map((id) => `${id}:${gid}`)
            )
          );
          const filtered = prev.filter((m) => !toRemove.has(`${m.photo_id}:${m.group_id}`));
          const existing = new Set(filtered.map((m) => `${m.photo_id}:${m.group_id}`));
          const newMemberships = toAssign
            .filter((id) => !existing.has(`${id}:${targetGroupId}`))
            .map((id) => ({ photo_id: id, group_id: targetGroupId }));
          return [...filtered, ...newMemberships];
        });

        const promises: Promise<unknown>[] = [];
        for (const [sourceGid, ids] of toUnassignByGroup) {
          promises.push(api.unassignMembers(userLid, sourceGid, ids));
        }
        promises.push(api.assignMembers(userLid, targetGroupId, toAssign));
        Promise.all(promises).catch(console.error);
        clearSelection();
      }

      if (isUngroupedTarget) {
        const toUnassignByGroup = new Map<string, string[]>();
        for (const id of draggedPhotoIds) {
          const sourceGroup = membershipByPhoto.get(id);
          if (sourceGroup) {
            const list = toUnassignByGroup.get(sourceGroup) || [];
            list.push(id);
            toUnassignByGroup.set(sourceGroup, list);
          }
        }

        if (toUnassignByGroup.size === 0) return;

        setMemberships((prev) => {
          const toRemove = new Set(
            Array.from(toUnassignByGroup.entries()).flatMap(([gid, ids]) =>
              ids.map((id) => `${id}:${gid}`)
            )
          );
          return prev.filter((m) => !toRemove.has(`${m.photo_id}:${m.group_id}`));
        });

        for (const [gid, ids] of toUnassignByGroup) {
          api.unassignMembers(userLid, gid, ids).catch(console.error);
        }
        clearSelection();
      }
    },
    [userLid, draggedPhotoIds, membershipByPhoto, memberships, allVisiblePhotoIds, setMemberships, clearSelection]
  );

  return {
    sensors,
    activePhotoId,
    draggedPhotoIds,
    onDragStart,
    onDragEnd,
    onDragCancel,
  };
}
